import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Box, CircularProgress } from '@mui/material';

import styled from '@emotion/styled';
import { type MRT_ColumnDef } from 'material-react-table';

import { venueGetConversationWithUserApi } from '../../../api/chat/venueGetConversationWithUserApi';
import {
  OfferCategory,
  Reservation,
  ReservationStatus,
  ShippingStatus,
} from '../../../graphql/API';
import { ReservationPaymentStatus } from '../../../pages/ReservationDetail/ReservationDetail';
import { extractSubscriptionPlan } from '../../../pages/SubscriptionPlans/common/helper';
import { useAppSelector } from '../../../redux/hooks';
import icons from '../../../themes/icons';
import Icons from '../../../themes/icons';
import { getDateAndTimeInTimezoneInDayjs } from '../../../utils/dateV2';
import {
  checkChatHoursIsNotPassed,
  getReservationStatus,
  getReservationTimeLeftToPost,
  getShippingAddressString,
} from '../../../utils/reservations';
import { getUserCategory } from '../../../utils/user';
import UserAvatarCategory from '../../Avatar/UserAvatarCategory/UserAvatarCategory';
import Button from '../../Button/Button';
import { ReservationPostLink } from '../../ReservationPostLink';
import { ReservationTimeLeftToPost } from '../../ReservationTimeLeftToPost';
import StatusLabel from '../../StatusLabel/StatusLabel';
import { StatusLabelV2 } from '../../StatusLabelV2/StatusLabelV2';

type UseColumnProps = {
  detail?: boolean;
  isOnlineBrand?: boolean;
  onAddTracking?: (reservation: Reservation) => void;
  onMarkAsShipped?: (reservation: Reservation) => void;
};

const CollabBadge = styled.span`
  display: inline-block;
  color: #7b7bff;
  font-size: 15px;
  font-weight: 400;
`;

const base: MRT_ColumnDef<Reservation>[] = [
  {
    accessorKey: 'userPicture.thumbnail',
    header: '',
    size: 80,
    enableSorting: false,
    Cell: ({ renderedCellValue, row }) =>
      renderedCellValue ? (
        <UserAvatarCategory
          src={String(row.original.userPicture?.thumbnail ?? '')}
          userCategory={getUserCategory(row.original.user?.tags ?? [])}
        />
      ) : null,
  },
  {
    accessorKey: 'modelName',
    accessorFn: (row) =>
      row.user?.firstname || row.user?.lastname
        ? `${row.user?.firstname} ${row.user?.lastname}`
        : undefined,
    header: 'Name',
    size: 150,
    Cell: ({ renderedCellValue, row }) => (
      <Box display={'flex'} alignItems={'flex-start'} gap={'8px'}>
        <Box
          component={Link}
          to={`/users/${row.original.userID}`}
          sx={{
            textDecoration: 'none',
            ':hover': {
              textDecoration: 'underline',
            },
            color: '#1E55AA',
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {renderedCellValue}
        </Box>
        {row.original.inviteID && <Icons.DollarSignV2Icon />}
      </Box>
    ),
  },
  {
    accessorKey: 'offerTitle',
    header: 'Offer',
    size: 210,
    Cell: ({ renderedCellValue }) => (
      <Box display={'flex'} alignItems={'center'} gap={'12px'}>
        <span>{renderedCellValue}</span>
      </Box>
    ),
  },
  {
    accessorKey: 'privateOffer',
    header: 'Collab',
    size: 130,
    Cell: ({ row }) => (row.original.privateOffer ? <CollabBadge>Collab</CollabBadge> : '-'),
  },
  {
    accessorKey: 'effectiveStartDateTime',
    header: 'Reservation date',
    size: 200,
    Cell: ({ row }) => (
      <>
        {getDateAndTimeInTimezoneInDayjs(
          row?.original?.effectiveStartDateTime as string,
          row?.original?.offerTimeZoneId && row?.original?.offerCategory === OfferCategory.event ? row?.original?.offerTimeZoneId : row?.original?.timeZoneId,
        )?.format('LL')}
      </>
    ),
  },
  {
    accessorKey: 'shippingStatus',
    header: 'Shipping',
    size: 150,
    Cell: ({ renderedCellValue }) => (
      <StatusLabel type='shippingStatus' status={String(renderedCellValue)} emptyBackground />
    ),
  },
  {
    accessorKey: 'shippedDate',
    header: 'Shipping date',
    size: 150,
    Cell: ({ row, renderedCellValue }) => (
      <span>
        {getDateAndTimeInTimezoneInDayjs(
          String(renderedCellValue),
          row?.original?.timeZoneId,
        )?.format('LL')}
      </span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 150,
    Cell: ({ renderedCellValue, row }) => (
      <StatusLabel
        type='reservation'
        status={
          row.original.requirementsSubmitted && renderedCellValue == ReservationStatus.checkedin
            ? 'completed'
            : String(renderedCellValue)
        }
        emptyBackground
      />
    ),
  },
  {
    accessorKey: 'postLink',
    header: 'Post link',
    size: 180,
    Cell: ({ row }) => <ReservationPostLink reservation={row.original} />,
  },
];

const action: MRT_ColumnDef<Reservation> = {
  accessorKey: 'id',
  header: 'Action',
  size: 120,
  enableSorting: false,
  Cell: ({ renderedCellValue, row }) => {
    const navigate = useNavigate();
    const { subscription } = useAppSelector((state) => state.subscription.value);
    const [isConversationCreationLoading, setIsConversationCreationLoading] = React.useState(false);

    const subscriptionPlan = React.useMemo(() => {
      const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

      return plan;
    }, [subscription]);

    const isChatCanStart = React.useMemo(
      () =>
        (row.original &&
          subscriptionPlan === 'plus' &&
          checkChatHoursIsNotPassed(row.original) &&
          row.original?.status !== ReservationStatus.cancelled) ||
        (checkChatHoursIsNotPassed(row.original) &&
          row.original?.status !== ReservationStatus.cancelled &&
          (!!row.original?.inviteID ||
            (row.original.offerCategory === OfferCategory.beauty &&
              row.original.offerConfirmWithCall))) ||
        (row.original &&
          !!row.original?.inviteID &&
          row.original.paymentStatus === ReservationPaymentStatus.stop) ||
        false,
      [row.original, subscriptionPlan],
    );

    const createConversationWithUser = async () => {
      try {
        setIsConversationCreationLoading(true);
        await venueGetConversationWithUserApi({
          venueID: row.original.venueID || '',
          userID: row.original?.userID || '',
          reservationID: row.original.id || '',
          offerTitle: row.original.offerTitle || '',
        });
        setIsConversationCreationLoading(false);
        navigate(
          `/chats?userID=${row?.original.userID}&reservationID=${row.original?.id}&offerTitle=${row.original.offerTitle}`,
        );
      } catch (error) {
        console.error(error);
        setIsConversationCreationLoading(false);
      }
    };

    return (
      <Box display={'flex'} alignItems={'center'} gap={'8px'}>
        <Button
          component={Link}
          to={`/reservations/${renderedCellValue}`}
          variant='outlined'
          size='small'
        >
          Details
        </Button>

        {isChatCanStart && (
          <Button
            variant='contained'
            size='small'
            startIcon={
              isConversationCreationLoading ? (
                <CircularProgress size='1rem' sx={{ color: 'grey' }} />
              ) : (
                <icons.ChatIcon style={{ width: '16px', height: '16px' }} />
              )
            }
            onClick={createConversationWithUser}
            disabled={isConversationCreationLoading}
          >
            Chat
          </Button>
        )}
      </Box>
    );
  },
};

const onlineBrandColumns: ({
  onAddTracking,
  onMarkAsShipped,
}: {
  onAddTracking: (reservation: Reservation) => void;
  onMarkAsShipped: (reservation: Reservation) => void;
}) => MRT_ColumnDef<Reservation>[] = ({ onAddTracking, onMarkAsShipped }) => [
  {
    accessorKey: 'userPicture.thumbnail',
    header: '',
    size: 80,
    enableSorting: false,
    Cell: ({ renderedCellValue, row }) =>
      renderedCellValue ? (
        <UserAvatarCategory
          src={String(row.original.userPicture?.thumbnail ?? '')}
          userCategory={getUserCategory(row.original.user?.tags ?? [])}
        />
      ) : null,
  },
  {
    accessorKey: 'modelName',
    accessorFn: (row) =>
      row.user?.firstname || row.user?.lastname
        ? `${row.user?.firstname} ${row.user?.lastname}`
        : undefined,
    header: 'Name',
    size: 150,
    Cell: ({ renderedCellValue, row }) => (
      <Box display={'flex'} alignItems={'flex-start'} gap={'8px'}>
        <Box
          component={Link}
          to={`/users/${row.original.userID}`}
          sx={{
            textDecoration: 'none',
            ':hover': {
              textDecoration: 'underline',
            },
            color: '#1E55AA',
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {renderedCellValue}
        </Box>
        {row.original.inviteID && <Icons.DollarSignV2Icon />}
      </Box>
    ),
  },
  {
    accessorKey: 'offerTitle',
    header: 'Product',
    size: 180,
    Cell: ({ renderedCellValue }) => (
      <Box display={'flex'} alignItems={'center'} gap={'12px'}>
        <span>{renderedCellValue}</span>
      </Box>
    ),
  },
  {
    accessorKey: 'effectiveStartDateTime',
    header: 'Reservation date',
    size: 170,
    Cell: ({ row }) => (
      <>
        {getDateAndTimeInTimezoneInDayjs(
          row?.original?.effectiveStartDateTime as string,
          row?.original?.timeZoneId,
        )?.format('LL')}
      </>
    ),
  },
  {
    accessorKey: 'shippingStatus',
    header: 'Address',
    size: 150,
    enableSorting: false,
    Cell: ({ row }) => (row.original.shippingAddress ? getShippingAddressString(row.original) : '-'),
  },
  {
    accessorKey: 'shippingStatus',
    header: 'Shipping status',
    size: 150,
    Cell: ({ row }) => (
      <StatusLabelV2 type='shippingStatus' status={getReservationStatus(row.original)} />
    ),
  },
  {
    accessorKey: 'effectiveEndDateTime',
    header: 'Promotion status',
    size: 150,
    Cell: ({ row }) => <ReservationTimeLeftToPost data={row.original} />,
  },
  {
    accessorKey: 'id',
    header: 'Action',
    size: 120,
    enableSorting: false,
    Cell: ({ renderedCellValue, row }) => {
      return getReservationTimeLeftToPost(row.original) === 'notCompleted' ? (
        <Button
          component={Link}
          to={`/reservations/${renderedCellValue}`}
          variant='outlined'
          size='medium'
          fullWidth
          sx={{ fontSize: '14px', fontWeight: 400, borderRadius: '6px', px: 0.2 }}
        >
          Details
        </Button>
      ) : row.original.postLink ? (
        <Button
          component={'a'}
          href={row.original.postLink}
          target='_blank'
          variant='contained'
          size='medium'
          fullWidth
          sx={{ fontSize: '14px', fontWeight: 400, borderRadius: '6px', px: 0.2 }}
          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
          }}
        >
          Go to post
        </Button>
      ) : row.original.shopifyOrder?.orderUrl ? (
        <Button
          component='a'
          href={row.original.shopifyOrder?.orderUrl}
          size='medium'
          variant='outlined'
          startIcon={<Icons.ShopifyIcon />}
          color='primary'
          sx={{ fontSize: '14px', fontWeight: 400, borderRadius: '6px', px: 0.2 }}
          target='_blank'
          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
          }}
          fullWidth
        >
          See order
        </Button>
      ) : row.original.shippingStatus === ShippingStatus.waiting &&
        !row.original.offer?.shopifyProductID ? (
        <Button
          variant='outlined'
          size='medium'
          fullWidth
          sx={{ fontSize: '14px', fontWeight: 400, borderRadius: '6px', px: 0.2 }}
          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            onMarkAsShipped(row.original);
          }}
        >
          Mark as shipped
        </Button>
      ) : row.original.shippingStatus === ShippingStatus.shipped &&
        !row.original.orderTracking?.trackingNumber &&
        !row.original.offer?.shopifyProductID ? (
        <Button
          startIcon={<Icons.AddIcon />}
          variant='outlined'
          size='medium'
          fullWidth
          sx={{ fontSize: '14px', fontWeight: 400, borderRadius: '6px', px: 0.2 }}
          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            onAddTracking(row.original);
          }}
        >
          Add tracking
        </Button>
      ) : (
        <Button
          component={Link}
          to={`/reservations/${renderedCellValue}`}
          variant='outlined'
          size='medium'
          fullWidth
          sx={{ fontSize: '14px', fontWeight: 400, borderRadius: '6px', px: 0.2 }}
        >
          Details
        </Button>
      );
    },
  },
];

const useColumn = ({
  detail,
  isOnlineBrand = false,
  onAddTracking = () => {},
  onMarkAsShipped = () => {},
}: UseColumnProps): MRT_ColumnDef<Reservation>[] => {
  const columns = React.useMemo<MRT_ColumnDef<Reservation>[]>(
    () => (detail ? base.concat(action) : base),
    [detail],
  );

  return isOnlineBrand
    ? onlineBrandColumns({ onAddTracking, onMarkAsShipped })
    : columns.filter(
        (column) => !['shippedDate', 'shippingStatus'].includes(column.accessorKey || ''),
      );
};

export default useColumn;
